/**
 * @file noteGrouping.ts
 * @brief Splits the notes library into the same sections as the phone.
 *
 * @details
 * A port of mobile's `FRONTEND/src/screens/Notes/noteGrouping.ts`, so a note
 * sits under the same heading on both surfaces: Pinned first (and not
 * repeated below), then Today, Yesterday, This Week, This Month, and the month
 * name, with the year added for an older year. The ladder is deliberately
 * coarse: by exact day, nearly every note earned its own header.
 *
 * Server order is kept inside each section. Re-sorting here would silently
 * diverge from what the next page appends.
 *
 * Pure and free of app imports, so `node --test` can load it directly.
 *
 * @author DES
 * @date 30-09-2026
 */

export type NoteSection<T> = { label: string; notes: T[] };

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * The section a note belongs to. Precedence matters: three days ago is inside
 * this week AND this month, and lands in the narrower one.
 */
export function noteDateLabel(createdAtMs: number, nowMs: number): string {
  const day = startOfDay(createdAtMs);
  const today = startOfDay(nowMs);
  if (day === today) return "Today";
  if (day === today - DAY_MS) return "Yesterday";
  if (day > today - 7 * DAY_MS) return "This Week";
  const d = new Date(createdAtMs);
  const now = new Date(nowMs);
  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameYear && d.getMonth() === now.getMonth()) return "This Month";
  return d.toLocaleDateString(undefined, {
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function groupNotes<
  T extends { is_pinned?: boolean | null; created_at?: string | null },
>(notes: T[], nowMs: number = Date.now()): NoteSection<T>[] {
  const out: NoteSection<T>[] = [];
  const pinned = notes.filter((n) => n.is_pinned);
  if (pinned.length) out.push({ label: "Pinned", notes: pinned });
  for (const note of notes) {
    if (note.is_pinned) continue;
    const ms = note.created_at ? Date.parse(note.created_at) : NaN;
    const label = Number.isFinite(ms) ? noteDateLabel(ms, nowMs) : "Earlier";
    const last = out[out.length - 1];
    // Consecutive, not a lookup: the server sorts newest first, so a section
    // is always one run. A lookup would pull a stray note up into an earlier
    // heading if the order ever changed.
    if (last && last.label === label && last.label !== "Pinned") {
      last.notes.push(note);
    } else {
      out.push({ label, notes: [note] });
    }
  }
  return out;
}
